const schema = (props) => {
  const { intl } = props;

  return {
    title: 'Hero Block',
    fieldsets: [
      {
        id: 'default',
        title: 'Hero Options',
        fields: [
          'isFullWidth',
          'multipleHeroes',
          'displayMode',
          'carouselEnabled',
          'carouselDisplayMode',
          'customClass',
        ],
      },
      {
        id: 'firstHero',
        title: 'First Hero',
        fields: [
          'usePageTitle',
          'title',
          'usePageDescription',
          'subtitle',
          'preheader',
          'showBreadcrumbs',
          'heroLogo',
          'logoPosition',
          'logoSize',
          'usePreviewImage',
          'backgroundImage',
          'backgroundVideo',
          'backgroundPosition',
          'backgroundColor',
          'foregroundColor',
          'showPublicationDate',
          'topPaddingDesktop',
          'topPaddingMobile',
          'alignment',
          'horizontalLayout',
          'useTOC',
          'useH2',
          'useH3',
          'useBlockTitles',
          'buttons',
          'smallButtons',
        ],
      },
      {
        id: 'heroes',
        title: 'Additional Heroes',
        fields: ['heroes'],
      },
      {
        id: 'carousel',
        title: 'Carousel Settings',
        fields: [
          'carouselAutoplay',
          'carouselAutoplayDelay',
          'carouselHideNav',
          'carouselHideButtons',
          'carouselControlsColor',
        ],
      },
    ],
    properties: {
      isFullWidth: {
        title: 'Full width background',
        type: 'boolean',
        default: false,
      },
      multipleHeroes: {
        title: 'Enable multiple heroes',
        type: 'boolean',
        default: false,
      },
      displayMode: {
        title: 'Display mode',
        type: 'string',
        choices: [
          ['individual', 'Individual backgrounds per hero'],
          ['shared', 'Shared background from first hero'],
        ],
        default: 'individual',
      },
      carouselEnabled: {
        title: 'Enable carousel mode',
        description: 'Display only additional heroes in a carousel (first hero is hidden)',
        type: 'boolean',
        default: false,
      },
      carouselDisplayMode: {
        title: 'Carousel background mode',
        type: 'string',
        choices: [
          ['individual', 'Individual backgrounds per slide'],
          ['shared', 'Shared background from first hero'],
        ],
        default: 'individual',
      },
      customClass: {
        title: 'Custom CSS class',
        type: 'string',
        description: 'Additional class to add to the hero block',
      },
      usePageTitle: {
        title: 'Use page title as first hero title',
        type: 'boolean',
        default: false,
      },
      title: {
        title: 'Title',
        type: 'string',
      },
      usePageDescription: {
        title: 'Use page description as first hero subtitle',
        type: 'boolean',
        default: false,
      },
      subtitle: {
        title: 'Subtitle',
        type: 'string',
      },
      preheader: {
        title: 'Preheader',
        type: 'string',
        description: 'Text that appears above the title',
      },
      showBreadcrumbs: {
        title: 'Show breadcrumbs',
        type: 'boolean',
        default: false,
        description: 'Displays breadcrumbs above the title and hides the site breadcrumbs',
      },
      heroLogo: {
        title: 'Logo',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
        description: 'Optional logo image to display in the hero',
      },
      logoPosition: {
        title: 'Logo position',
        choices: [
          ['above-title', 'Above title'],
          ['beside-content', 'Beside content (right side)'],
        ],
        default: 'above-title',
      },
      logoSize: {
        title: 'Logo size',
        choices: [
          ['small', 'Small'],
          ['medium', 'Medium'],
          ['large', 'Large'],
        ],
        default: 'medium',
      },
      usePreviewImage: {
        title: 'Use page preview image for first hero',
        type: 'boolean',
        default: false,
      },
      backgroundImage: {
        title: 'Background image',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
      },
      backgroundVideo: {
        title: 'Background video',
        widget: 'object_browser',
        mode: 'file',
        allowExternals: false,
        selectableTypes: ['File'],
        description: 'MP4 only. Will autoplay, loop, and stay muted.',
      },
      backgroundPosition: {
        title: 'Background image position',
        choices: [
          ['top', 'Top'],
          ['center', 'Center'],
          ['bottom', 'Bottom'],
        ],
        default: 'center',
      },
      backgroundColor: {
        title: 'Background color overlay',
        choices: [
          ['', 'Gradient (default)'],
          ['none', 'None'],
          ['rgba(0, 0, 0, 0.3)', 'Black - Light (30%)'],
          ['rgba(0, 0, 0, 0.5)', 'Black - Medium (50%)'],
          ['rgba(0, 0, 0, 0.7)', 'Black - Dark (70%)'],
          ['rgba(255, 255, 255, 0.3)', 'White - Light (30%)'],
          ['rgba(255, 255, 255, 0.5)', 'White - Medium (50%)'],
          ['rgba(255, 255, 255, 0.7)', 'White - Dark (70%)'],
          ['rgba(var(--accent-color-rgb),0.3)', 'Primary - Light (30%)'],
          ['rgba(var(--accent-color-rgb),0.5)', 'Primary - Medium (50%)'],
          ['rgba(var(--accent-color-rgb),0.7)', 'Primary - Dark (70%)'],
        ],
      },
      foregroundColor: {
        title: 'Foreground color',
        description: 'Color scheme for text and buttons',
        choices: [
          ['light', 'Light (white text)'],
          ['dark', 'Dark (black text)'],
        ],
        default: 'light',
      },
      showPublicationDate: {
        title: 'Show publication date on first hero',
        type: 'boolean',
        default: false,
      },
      topPaddingDesktop: {
        title: 'Top padding (Desktop)',
        choices: [
          ['compact', 'Compact (4rem)'],
          ['default', 'Default (8rem)'],
          ['spacious', 'Spacious (12rem)'],
          ['extra-spacious', 'Extra Spacious (16rem)'],
        ],
        default: 'default',
      },
      topPaddingMobile: {
        title: 'Top padding (Mobile)',
        choices: [
          ['compact', 'Compact (4rem)'],
          ['default', 'Default (8rem)'],
          ['spacious', 'Spacious (12rem)'],
        ],
        default: 'default',
      },
      alignment: {
        title: 'Text alignment',
        widget: 'align',
        actions: ['left', 'center', 'right'],
        default: 'left',
      },
      horizontalLayout: {
        title: 'Display text and buttons side by side',
        type: 'boolean',
        default: false,
      },
      useTOC: {
        title: 'Use TOC instead of buttons',
        type: 'boolean',
        default: false,
      },
      useH2: {
        title: 'Include H2 headings in TOC',
        type: 'boolean',
        default: true,
      },
      useH3: {
        title: 'Include H3 headings in TOC',
        type: 'boolean',
        default: true,
      },
      useBlockTitles: {
        title: 'Include block titles in TOC',
        description: 'Include titles from listing blocks, carousels, and other blocks',
        type: 'boolean',
        default: false,
      },
      buttons: {
        title: 'Buttons',
        widget: 'object_list',
        schema: {
          title: 'Button',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['label', 'link', 'buttonStyle'],
            },
          ],
          properties: {
            label: {
              title: 'Label',
              type: 'string',
            },
            link: {
              title: 'Link',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
              multi: false,
              default: null,
            },
            buttonStyle: {
              title: 'Button style',
              choices: [
                ['white-outline', 'White outline'],
                ['white-filled', 'White filled'],
                ['black-outline', 'Black outline'],
                ['black-filled', 'Black filled'],
                ['primary-outline', 'Primary outline'],
                ['primary-filled', 'Primary filled'],
              ],
              default: 'white-outline',
            },
          },
          required: [],
        },
      },
      smallButtons: {
        title: 'Use smaller buttons',
        type: 'boolean',
        default: false,
      },
      heroes: {
        title: 'Heroes',
        widget: 'object_list',
        schema: {
          title: 'Hero',
          fieldsets: [
            {
              id: 'content',
              title: 'Content',
              fields: ['preheader', 'title', 'subtitle', 'heroLogo', 'logoPosition', 'logoSize', 'topPaddingDesktop', 'topPaddingMobile', 'alignment', 'horizontalLayout', 'buttons', 'smallButtons'],
            },
            {
              id: 'background',
              title: 'Background',
              fields: ['backgroundImage', 'backgroundVideo', 'backgroundPosition', 'backgroundColor', 'foregroundColor'],
            },
          ],
          properties: {
            preheader: {
              title: 'Preheader',
              type: 'string',
            },
            title: {
              title: 'Title',
              type: 'string',
            },
            subtitle: {
              title: 'Subtitle',
              type: 'string',
            },
            heroLogo: {
              title: 'Logo',
              widget: 'object_browser',
              mode: 'image',
              allowExternals: false,
              description: 'Optional logo image to display in the hero',
            },
            logoPosition: {
              title: 'Logo position',
              choices: [
                ['above-title', 'Above title'],
                ['beside-content', 'Beside content (right side)'],
              ],
              default: 'above-title',
            },
            logoSize: {
              title: 'Logo size',
              choices: [
                ['small', 'Small'],
                ['medium', 'Medium'],
                ['large', 'Large'],
              ],
              default: 'medium',
            },
            topPaddingDesktop: {
              title: 'Top padding (Desktop)',
              choices: [
                ['compact', 'Compact (4rem)'],
                ['default', 'Default (8rem)'],
                ['spacious', 'Spacious (12rem)'],
                ['extra-spacious', 'Extra Spacious (16rem)'],
              ],
              default: 'default',
            },
            topPaddingMobile: {
              title: 'Top padding (Mobile)',
              choices: [
                ['compact', 'Compact (4rem)'],
                ['default', 'Default (8rem)'],
                ['spacious', 'Spacious (12rem)'],
              ],
              default: 'default',
            },
            alignment: {
              title: 'Text alignment',
              widget: 'align',
              actions: ['left', 'center', 'right'],
              default: 'left',
            },
            horizontalLayout: {
              title: 'Display text and buttons side by side',
              type: 'boolean',
              default: false,
            },
            buttons: {
              title: 'Buttons',
              widget: 'object_list',
              schema: {
                title: 'Button',
                fieldsets: [
                  {
                    id: 'default',
                    title: 'Default',
                    fields: ['label', 'link', 'buttonStyle'],
                  },
                ],
                properties: {
                  label: {
                    title: 'Label',
                    type: 'string',
                  },
                  link: {
                    title: 'Link',
                    widget: 'object_browser',
                    mode: 'link',
                    allowExternals: true,
                    multi: false,
                    default: null,
                  },
                  buttonStyle: {
                    title: 'Button style',
                    choices: [
                      ['white-outline', 'White outline'],
                      ['white-filled', 'White filled'],
                      ['black-outline', 'Black outline'],
                      ['black-filled', 'Black filled'],
                      ['primary-outline', 'Primary outline'],
                      ['primary-filled', 'Primary filled'],
                    ],
                    default: 'white-outline',
                  },
                },
                required: [],
              },
            },
            smallButtons: {
              title: 'Use smaller buttons',
              type: 'boolean',
              default: false,
            },
            backgroundImage: {
              title: 'Background image',
              widget: 'object_browser',
              mode: 'image',
              allowExternals: false,
            },
            backgroundVideo: {
              title: 'Background video',
              widget: 'object_browser',
              mode: 'file',
              allowExternals: false,
              selectableTypes: ['File'],
              description: 'MP4 only. Will autoplay, loop, and stay muted.',
            },
            backgroundPosition: {
              title: 'Background image position',
              choices: [
                ['top', 'Top'],
                ['center', 'Center'],
                ['bottom', 'Bottom'],
              ],
              default: 'center',
            },
            backgroundColor: {
              title: 'Background color overlay',
              choices: [
                ['', 'Gradient (default)'],
                ['none', 'None'],
                ['rgba(0, 0, 0, 0.3)', 'Black - Light (30%)'],
                ['rgba(0, 0, 0, 0.5)', 'Black - Medium (50%)'],
                ['rgba(0, 0, 0, 0.7)', 'Black - Dark (70%)'],
                ['rgba(255, 255, 255, 0.3)', 'White - Light (30%)'],
                ['rgba(255, 255, 255, 0.5)', 'White - Medium (50%)'],
                ['rgba(255, 255, 255, 0.7)', 'White - Dark (70%)'],
                ['rgba(var(--accent-color-rgb),0.3)', 'Primary - Light (30%)'],
                ['rgba(var(--accent-color-rgb),0.5)', 'Primary - Medium (50%)'],
                ['rgba(var(--accent-color-rgb),0.7)', 'Primary - Dark (70%)'],
              ],
            },
            foregroundColor: {
              title: 'Foreground color',
              description: 'Color scheme for text and buttons',
              choices: [
                ['light', 'Light (white text)'],
                ['dark', 'Dark (black text)'],
              ],
              default: 'light',
            },
          },
          required: [],
        },
      },
      carouselAutoplay: {
        title: 'Enable autoplay',
        type: 'boolean',
        default: false,
      },
      carouselAutoplayDelay: {
        title: 'Autoplay delay (milliseconds)',
        type: 'number',
        default: 5000,
        description: 'Time between automatic slide transitions',
      },
      carouselHideNav: {
        title: 'Hide navigation dots',
        type: 'boolean',
        default: false,
      },
      carouselHideButtons: {
        title: 'Hide prev/next buttons',
        type: 'boolean',
        default: false,
      },
      carouselControlsColor: {
        title: 'Controls color scheme',
        choices: [
          ['light', 'Light (white)'],
          ['dark', 'Dark (black)'],
        ],
        default: 'light',
      },
    },
    required: [],
  };
};

export default schema;
